import useDocumentMeta from "../hooks/useDocumentMeta.js";
import usePageLanguage from "../hooks/usePageLanguage.js";
import { alternatesFor, canonicalUrlFor } from "../content/pages/registry.js";
import usePageShell from "../hooks/usePageShell.js";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import CookieBanner from "../components/common/CookieBanner";
import PhotoSharingHero from "../components/wedding-photo-sharing/PhotoSharingHero";
import PhotoSharingSteps from "../components/wedding-photo-sharing/PhotoSharingSteps";
import PhotoSharingEditorial from "../components/wedding-photo-sharing/PhotoSharingEditorial";
import PhotoSharingQr from "../components/wedding-photo-sharing/PhotoSharingQr";
import PhotoSharingArc from "../components/wedding-photo-sharing/PhotoSharingArc";
import PhotoSharingExperience from "../components/wedding-photo-sharing/PhotoSharingExperience";
import PhotoSharingFaq from "../components/wedding-photo-sharing/PhotoSharingFaq";
import PhotoSharingFinalCta from "../components/wedding-photo-sharing/PhotoSharingFinalCta";

/**
 * /wedding-photo-sharing
 *
 * A brand page that happens to answer a search query, not a search page wearing
 * brand clothes: photo sharing is the way in, and the arc + platform sections
 * put it back inside the whole Agapaé experience.
 *
 * ── Crawlability ──────────────────────────────────────────────────────────
 * This route also ships as a real static document (`wedding-photo-sharing.html`
 * at the project root, built as a second Vite entry). That file — not this
 * component — is what a crawler that does not run JavaScript receives, and it
 * carries the title, description, canonical, robots directive, social tags,
 * JSON-LD and every heading, paragraph and FAQ answer below. React replaces it
 * on mount with the designed page. `weddingPhotoSharing.seo.test.mjs` asserts
 * the two never drift apart.
 *
 * The meta hook still runs, because a visitor who arrives by client-side
 * navigation gets no static head.
 *
 * ── Page shell ────────────────────────────────────────────────────────────
 * `usePageShell` — the shared bootstrap every page runs. See the hook for why
 * it re-initialises the Webflow engine from a layout effect rather than after
 * paint.
 */
/**
 * This page's language-neutral path. The canonical and the alternates are
 * derived from it through the page registry rather than written out: the
 * canonical is self-referencing (a Greek page canonicalised to English is a
 * Greek page asking not to be indexed), and the alternates are the reciprocal
 * set the static documents declare, from the same one source.
 */
const PATH = "/wedding-photo-sharing";
const STEPS_SECTION_ID = "how-it-works";

const WeddingPhotoSharing = () => {
  const language = usePageLanguage();

  useDocumentMeta({
    titleKey: "meta.weddingPhotoSharing.title",
    descriptionKey: "meta.weddingPhotoSharing.description",
    canonical: canonicalUrlFor(PATH, language),
    alternates: alternatesFor(PATH),
    robots: "index, follow",
  });

  usePageShell();

  return (
    <div className="page-wrapper wps-page">
      <Navbar />
      <CookieBanner />
      <main className="main">
        <PhotoSharingHero secondarySectionId={STEPS_SECTION_ID} />

        <PhotoSharingSteps id={STEPS_SECTION_ID} />

        <PhotoSharingEditorial
          headingKey="weddingPhotoSharing.noApp.heading"
          bodyKeys={["weddingPhotoSharing.noApp.body"]}
        />

        <PhotoSharingEditorial
          headingKey="weddingPhotoSharing.perspective.heading"
          bodyKeys={[
            "weddingPhotoSharing.perspective.body1",
            "weddingPhotoSharing.perspective.body2",
          ]}
          image={{
            src: "/assets/images/moments/moment_1.jpg",
            altKey: "weddingPhotoSharing.perspective.imageAlt",
          }}
        />

        <PhotoSharingEditorial
          headingKey="weddingPhotoSharing.media.heading"
          bodyKeys={["weddingPhotoSharing.media.body"]}
          noteKey="weddingPhotoSharing.media.note"
        />

        <PhotoSharingEditorial
          headingKey="weddingPhotoSharing.privacy.heading"
          bodyKeys={["weddingPhotoSharing.privacy.body"]}
        />

        <PhotoSharingQr />

        <PhotoSharingEditorial
          headingKey="weddingPhotoSharing.gallery.heading"
          bodyKeys={["weddingPhotoSharing.gallery.body"]}
          noteKey="weddingPhotoSharing.gallery.note"
          image={{
            src: "/assets/images/bride_shoes.jpg",
            altKey: "weddingPhotoSharing.gallery.imageAlt",
          }}
          imageSide="left"
        />

        <PhotoSharingArc />

        <PhotoSharingExperience />

        <PhotoSharingFaq />

        <PhotoSharingFinalCta />
      </main>
      <Footer />
    </div>
  );
};

export default WeddingPhotoSharing;
